import { useMemo, useState, type ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { formatDuration, formatShortDuration } from '@/lib/time';
import { useTicker } from '@/lib/use-ticker';
import { errorCopy } from '../copy/errors';
import { PHASE_SEQUENCE, phaseDef, phaseIndex } from './phases';
import { RunGlyph } from './Rail';
import { segmentDuration, stopDuration, type LedgerSegment, type LedgerStop } from './projection';

/**
 * One run, as a block in the thread.
 *
 * The default reading is a single line: what is happening to their product, and
 * how long it has taken. The stages are one press away, because a client who is
 * happy to wait should not have to scroll past six timed rows to reach the next
 * thing the agent said — and a client who is not happy to wait wants exactly
 * those six rows.
 *
 * A live run opens by default. That is the one case where the stages *are* the
 * content: the run is why the person is looking at the screen, and collapsing
 * it would leave them watching a still line.
 *
 * The stages come from the neutral projection in `projection.ts` — never from
 * the agent's own words — and every duration on them is measured. Stages the
 * run has not reached yet are drawn dimmed with an em dash rather than a
 * guessed time: the ledger's whole value is that nothing on it is invented.
 */

interface HeadCopy {
  title: string;
  meta: string;
}

function headCopy(stop: LedgerStop, elapsed: number, live: boolean, current: LedgerSegment | null): HeadCopy {
  const stepNumber = phaseIndex(stop.currentPhase) + 1;
  const stepCount = PHASE_SEQUENCE.length;
  const error = stop.errorCode ? errorCopy(stop.errorCode) : null;

  if (live && stop.waiting) {
    return {
      title: stop.waiting.answerableByClient ? 'Paused for your answer' : 'Paused — your reviewer is answering',
      meta: `step ${stepNumber} of ${stepCount} · ${formatDuration(elapsed)}`,
    };
  }
  if (live) {
    return {
      title: current?.label ?? 'Working on it',
      meta: `step ${stepNumber} of ${stepCount} · ${formatDuration(elapsed)}`,
    };
  }
  if (stop.outcome === 'succeeded') {
    return {
      title: 'Finished',
      meta: `all ${stop.segments.length} steps · ${formatDuration(elapsed)}`,
    };
  }
  if (stop.outcome === 'cancelled') {
    return { title: 'Cancelled — nothing was published', meta: formatDuration(elapsed) };
  }
  if (stop.outcome === 'expired') {
    return { title: 'Stopped on its own — nothing was published', meta: formatDuration(elapsed) };
  }
  return {
    title: error?.title ?? 'Stopped — nothing was published',
    meta: `stopped at step ${Math.max(1, stop.segments.length)} · ${formatDuration(elapsed)}`,
  };
}

export function RunStop({
  stop,
  index,
  children,
}: {
  stop: LedgerStop;
  /** 1-based position in the conversation. Runs really are a sequence. */
  index: number;
  /** Agent turns, notices and the ask card belonging to this run. */
  children?: ReactNode;
}) {
  const live = stop.outcome === 'running';
  const now = useTicker(live);
  const elapsed = stopDuration(stop, now);
  const [open, setOpen] = useState(live);

  const current = stop.segments[stop.segments.length - 1] ?? null;
  const head = headCopy(stop, elapsed, live, current);
  const failed = stop.outcome === 'failed' || stop.outcome === 'expired';

  // The stages the run has not reached yet. Only while it is still running: on
  // a stopped run they are not "pending", they are stages that never happened.
  const pending = useMemo(() => {
    if (!live) return [];
    const reached = phaseIndex(stop.currentPhase);
    return PHASE_SEQUENCE.slice(reached + 1);
  }, [live, stop.currentPhase]);

  const stepsId = `run-${stop.runId}-steps`;

  return (
    <li className="animate-fade-rise">
      <div className={cn('mb-[18px]')}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls={stepsId}
          className={cn(
            'flex w-full items-center gap-3 rounded-lg px-[18px] py-3.5 text-left transition-colors',
            live && !failed ? 'bg-blush' : failed ? 'bg-alarm-wash' : 'bg-bone hover:bg-mint',
          )}
        >
          <RunGlyph outcome={stop.outcome} />
          <span className="min-w-0 flex-1">
            <span
              className={cn(
                'block text-[0.96875rem] font-semibold tracking-[-0.015em]',
                live && !failed && 'text-signal',
                failed && 'text-alarm',
                !live && !failed && 'text-graphite-900',
              )}
            >
              {head.title}
            </span>
            <span className="mt-0.5 block font-mono text-[0.6875rem] tabular-nums text-graphite-300">
              Request {String(index).padStart(2, '0')} · {head.meta}
            </span>
          </span>
          <span className="shrink-0 font-mono text-[0.78125rem] font-medium text-graphite-300">
            {open ? 'Hide steps' : 'Show steps'}
          </span>
        </button>

        {open ? (
          <ol id={stepsId} className="px-[18px] pb-0.5 pt-1.5">
            {stop.segments.map((segment, i) => {
              const isLast = i === stop.segments.length - 1;
              const isLive = live && isLast;
              const stopped = failed && isLast;
              const duration = segmentDuration(segment, now);
              const def = phaseDef(segment.phase);

              return (
                <li
                  key={`${segment.phase}-${segment.enteredAt}-${i}`}
                  className="flex items-baseline gap-3 py-[7px]"
                >
                  <span
                    className={cn(
                      'text-[0.90625rem]',
                      isLive && 'font-semibold text-signal',
                      stopped && 'text-alarm',
                      !isLive && !stopped && 'text-graphite-500',
                    )}
                  >
                    {segment.label}
                  </span>
                  {isLive ? (
                    <span className="hidden text-[0.8125rem] text-graphite-300 sm:inline">{def.hint}</span>
                  ) : null}
                  {isLive && segment.progress !== null ? (
                    <span className="font-mono text-[0.71875rem] tabular-nums text-signal">
                      {Math.round(segment.progress * 100)}%
                    </span>
                  ) : null}
                  <span className="ml-auto font-mono text-[0.71875rem] tabular-nums text-graphite-300">
                    {formatShortDuration(duration)}
                  </span>
                </li>
              );
            })}

            {pending.map((def) => (
              <li key={`pending-${def.phase}`} className="flex items-baseline gap-3 py-[7px] opacity-45">
                <span className="text-[0.90625rem] text-graphite-500">{def.label}</span>
                <span className="ml-auto font-mono text-[0.71875rem] tabular-nums text-graphite-300">—</span>
              </li>
            ))}
          </ol>
        ) : null}
      </div>

      {/* Waiting on a person. The run is parked, not stuck, and saying which
          matters: a client watching a still timer needs to know it is their move. */}
      {stop.waiting ? (
        <p className="mb-[18px] flex items-center gap-2 px-0.5 text-[0.90625rem] font-semibold text-signal">
          <span aria-hidden className="h-[7px] w-[7px] animate-ledger-pulse rounded-full bg-signal" />
          {stop.waiting.answerableByClient ? 'Waiting for your answer below' : 'Waiting on your reviewer'}
        </p>
      ) : null}

      {/* Everything the run said, and anything it asked. Prose sits on the
          ground beside the block, not in a card — cards are for artifacts. */}
      {children}

      {/* THE FAILURE BODY IS NOT REPEATED HERE. The platform writes a durable
          failure notice into the thread for the same error code, and that notice
          renders the same title and body from the same file — so drawing it
          again beside the run block put the identical two sentences on screen
          twice for one failure. The block keeps the one line that is its own. */}
    </li>
  );
}
